import { useEffect, useMemo } from 'react';
import ViewWrapperWithCommandBar from '../Layout/ViewWrapperWithCommandBar';
import { DefaultPalette, ICommandBarItemProps, Icon, TextField, Toggle } from '@fluentui/react';
import { buttonStylesMarginX, titleCommandBarButtonStyles } from '../../styles/button-styles';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { routes } from '../../common/constants';
import TimeTaxSupportSystemClient from '../../system';
import MessageCourier from '../../lib/MessageCourier';
import { User } from '../../system/User';
import { FieldValues, useForm } from 'react-hook-form';
import OrganizationalStructureIndividual from './OrganizationalStructureIndividual';

interface ICompanyTreeProps {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (change: boolean) => void;
  user: User;
}

export default function CostCenterCreateView(props: ICompanyTreeProps) {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    mode: 'onChange',
  });

  useEffect(() => {
    register('isCore', { value: false });
    register('costCenterName', { value: '' });
  }, [register]);

  const itemsMenu = useMemo(
    (): ICommandBarItemProps[] => [
      {
        key: 'title',
        text: t('Cecos y Core'),
        iconProps: {
          iconName: 'Backlog',
          color: DefaultPalette.themeLighterAlt,
          className: 'text-title-btn',
        },
        buttonStyles: titleCommandBarButtonStyles,
        className: 'text-title-btn',
      },
      {
        key: 'save',
        text: t('Guardar'),
        iconProps: { iconName: 'Save', color: DefaultPalette.themeLighterAlt },
        buttonStyles: buttonStylesMarginX,
        onClick: () => void handleSave(),
      },
      {
        key: 'back',
        text: t('Volver'),
        iconProps: {
          iconName: 'Back',
          color: DefaultPalette.themeLighterAlt,
          className: 'text-title-btn',
        },
        buttonStyles: titleCommandBarButtonStyles,
        className: 'text-title-btn',
        onClick: () => {
          navigate(`${routes.cost_center}`);
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleSave = handleSubmit(async (data: FieldValues) => {
    if (!data['Estructura Organizacional']) {
      return props.courier.messageError('Complete todo los campos requeridos');
    }

    const orgSystem = props.system.getOrganizationChartSystem();
    props.changeBlockUI(true);
    try {
      await orgSystem.createOrganigramaAndCostCenters({
        isCore: data.isCore,
        costCenterName: data.costCenterName,
        'Estructura Organizacional': data['Estructura Organizacional'],
      });
      navigate(routes.cost_center);
      props.courier.messageSuccess(t('Los cambios se guardaron de manera exitosa'));
    } catch (error) {
      console.log(error);
      props.courier.messageError(t('Error al guardar los cambios'));
    } finally {
      props.changeBlockUI(false);
    }
  });

  return (
    <ViewWrapperWithCommandBar title="" items={itemsMenu} withBorderBottom>
      <div className="padding-1">
        <p className="titulo_form_create padding-1">
          <Icon iconName="Org" style={{ fontSize: '25px' }} />
          {t('Nueva Estructura Organizacional')}
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: 15, maxWidth: 880 }}>
          <OrganizationalStructureIndividual
            system={props.system}
            watch={watch}
            register={register}
            errors={errors}
            setValue={setValue}
            changeBlockUI={props.changeBlockUI}
          />

          <div className="ms-Grid padding-1" style={{ flex: 1, minWidth: 300 }}>
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-sm6">
                <Toggle
                  label={t('Core')}
                  checked={watch('isCore', true)}
                  onChange={(_, checked) => {
                    setValue('isCore', checked);
                  }}
                />
              </div>

              <div className="ms-Grid-col ms-sm6">
                <TextField
                  label={t('Centro de costos')}
                  {...register('costCenterName', {
                    required: t('El centro de costos no puede estar vacío.').toString(),
                    pattern: {
                      value: /^[0-9]+$/,
                      message: t('Solo se permiten números').toString(),
                    },
                    setValueAs: (value) => value?.trim(),
                  })}
                />
                {errors.costCenterName && (
                  <span className="error_stilos_Organizacional">{t(errors.costCenterName?.message?.toString() as string)}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ViewWrapperWithCommandBar>
  );
}
